/**
 * Google Analytics 4 custom event tracking utilities
 * Tracks key platform engagement events: listing views, offers sent, reviews submitted
 */

const isGAReady = () => typeof window.gtag === 'function';

/**
 * Track when a user views a listing detail page
 * @param {string} listingId
 * @param {string} listingTitle
 * @param {string} category
 */
export function trackListingView(listingId, listingTitle, category) {
  if (!isGAReady()) return;
  window.gtag('event', 'listing_view', {
    listing_id: listingId,
    listing_title: listingTitle,
    category: category,
  });
}

/**
 * Track when a user sends a trade offer / proposal
 * @param {string} listingId
 * @param {string} listingTitle
 * @param {string} offerType - e.g. 'trade', 'service', 'auction'
 */
export function trackOfferSent(listingId, listingTitle, offerType) {
  if (!isGAReady()) return;
  window.gtag('event', 'offer_sent', {
    listing_id: listingId,
    listing_title: listingTitle,
    offer_type: offerType,
  });
}

/**
 * Track when a user submits a review
 * @param {string} revieweeId
 * @param {number} rating
 * @param {string} transactionId
 */
export function trackReviewSubmitted(revieweeId, rating, transactionId) {
  if (!isGAReady()) return;
  window.gtag('event', 'review_submitted', {
    reviewee_id: revieweeId,
    rating: rating,
    transaction_id: transactionId,
  });
}

/**
 * Track user registration
 * @param {string} method - e.g. 'email', 'google'
 */
export function trackSignUp(method) {
  if (!isGAReady()) return;
  window.gtag('event', 'sign_up', { method });
}

/**
 * Track user login
 * @param {string} method
 */
export function trackLogin(method) {
  if (!isGAReady()) return;
  window.gtag('event', 'login', { method });
}

/**
 * Track listing creation
 * @param {string} category
 * @param {string} exchangeType
 */
export function trackListingCreated(category, exchangeType) {
  if (!isGAReady()) return;
  window.gtag('event', 'listing_created', {
    category,
    exchange_type: exchangeType,
  });
}

/**
 * Track JavaScript errors and crashes
 * @param {string} description - Error message or description
 * @param {boolean} fatal - Whether the error is fatal (crashed the app)
 * @param {string} [source] - File or component where the error occurred
 */
export function trackException(description, fatal = false, source = '') {
  if (!isGAReady()) return;
  window.gtag('event', 'exception', {
    description: description?.substring(0, 150) || 'Unknown error',
    fatal: fatal,
    error_source: source || 'unknown',
  });
}

/**
 * Track unhandled Promise rejections
 * @param {string} reason - Rejection reason/message
 */
export function trackUnhandledRejection(reason) {
  if (!isGAReady()) return;
  window.gtag('event', 'exception', {
    description: ('Unhandled rejection: ' + (reason || 'unknown'))?.substring(0, 150),
    fatal: false,
    error_source: 'promise',
  });
}
